import React from "react";
import { motion } from "framer-motion";
import { Calendar, ArrowRight, MapPin, Users } from "lucide-react";
import NoImage from "../../../assets/images/No_Image_Available.jpg";
import DateFormatterComponent from "../../../utils/DateFormatterComponent";

const ExploreProgrammeCard = ({ programme, index, handleClick }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -8 }}
      className="group relative bg-white rounded-2xl overflow-hidden border-2 border-mamba-sky-blue/20 hover:border-mamba-golden transition-all duration-300 hover:shadow-2xl cursor-pointer h-full flex flex-col"
      onClick={() => handleClick(programme?.slug)}
    >
      {/* Image Section */}
      <div className="relative h-56 overflow-hidden">
        <motion.img
          whileHover={{ scale: 1.1 }}
          transition={{ duration: 0.6 }}
          src={programme?.display_image_url ?? NoImage}
          className="w-full h-full object-cover"
          alt={programme?.name}
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-slate-900/20 to-transparent" />
        
        {/* Badge */}
        <div className="absolute top-4 left-4">
          <div className="px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-full flex items-center gap-2 shadow-lg">
            <div className="w-2 h-2 rounded-full bg-mamba-golden animate-pulse" />
            <span className="text-xs font-bold text-mamba-navy uppercase">Available</span>
          </div>
        </div>

        {/* Quick Action Button */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }}
          whileHover={{ opacity: 1, scale: 1 }}
          className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300"
        >
          <div className="w-10 h-10 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-lg hover:bg-mamba-golden hover:text-mamba-navy transition-colors">
            <ArrowRight className="w-5 h-5" />
          </div>
        </motion.div>
      </div>

      {/* Content Section */}
      <div className="flex-1 p-6 flex flex-col">
        {/* Title */}
        <h3 className="text-xl font-black font-montserrat text-mamba-navy mb-3 group-hover:text-mamba-sky-blue transition-colors duration-300 line-clamp-2">
          {programme?.name}
        </h3>

        {/* Description */}
        <p className="text-slate-600 font-inter text-sm leading-relaxed mb-4 flex-1 line-clamp-3">
          {programme?.description}
        </p>

        {/* Dates */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-sm">
            <div className="flex items-center gap-1.5 text-mamba-sky-blue">
              <Calendar className="w-4 h-4" />
              <span className="font-semibold text-mamba-navy">Start:</span>
            </div>
            <span className="font-medium text-slate-600">
              <DateFormatterComponent date={programme?.start_date} />
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <div className="flex items-center gap-1.5 text-mamba-golden">
              <Calendar className="w-4 h-4" />
              <span className="font-semibold text-mamba-navy">End:</span>
            </div>
            <span className="font-medium text-slate-600">
              <DateFormatterComponent date={programme?.end_date} />
            </span>
          </div>
        </div>

        {/* Footer - Action Button */}
        <div className="pt-4 border-t border-slate-100">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full py-3 bg-gradient-to-r from-mamba-sky-blue to-mamba-navy text-white font-bold font-montserrat rounded-xl shadow-lg group-hover:shadow-mamba-sky-blue/50 transition-all duration-300 flex items-center justify-center gap-2"
          >
            View Details
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </motion.button>
        </div>
      </div>

      {/* Hover Effect Border */}
      <div className="absolute inset-0 rounded-2xl ring-2 ring-mamba-golden ring-opacity-0 group-hover:ring-opacity-100 transition-all duration-300 pointer-events-none" />
    </motion.div>
  );
};

export default ExploreProgrammeCard;
